import { memo, useCallback, useMemo } from 'react'
import { CrewButton } from 'components/elements/crewButton/crewButton'
import { TaskEntryDialog } from 'features/task/components/taskEntryDialog/taskEntryDialog'
import { MyTaskListSearchPanel } from './components/myTaskListSearchPanel/myTaskListSearchPanel'
import { useTranslation } from '@crew/modules/i18n'
import { useCrewNavigate } from 'hooks/useCrewNavigate'
import { useModal } from 'components/layouts/modal/useModal'
import { getDefaultTabValue } from '@crew/utils/enum'
import { MyTaskDisplayModeButtonGroup, TaskDetailListTabs } from 'enums/app'
import { TaskEntryFormMode } from 'features/task/components/taskEntryDialog/components/taskEntryForm/taskEntryForm'
import { CrewButtonGroup } from 'components/elements/crewButtonGroup/crewButtonGroup'
import { useAppDispatch, useAppSelector } from 'states/hooks'
import { setSelectedMyTaskListDisplayMode } from 'features/home/states/homeSlice'
import { CrewGuideToolTip } from 'components/devextreme/crewGuideToolTip/crewGuideToolTip'

type ButtonGroupInfo = {
  id: string
  text: string | undefined
  icon: JSX.Element | undefined
}

export const MyTaskListToolbar = memo(() => {
  const { t } = useTranslation()

  const { navigate } = useCrewNavigate()

  const dispatch = useAppDispatch()
  // 選択中のタスクリスト表示モードを取得
  const selectedTaskListDisplayMode = useAppSelector(
    (state) => state.home.selectedMyTaskListDisplayMode
  )

  const [isTaskEntryDialogOpen, openTaskEntryDialog, closeTaskEntryDialog] =
    useModal()

  // タスク登録完了時
  const handleTaskRegistered = useCallback(
    (taskId: string) => {
      closeTaskEntryDialog()

      // タスク詳細画面に遷移
      navigate(`/tasks/${taskId}/${getDefaultTabValue(TaskDetailListTabs)}`)
    },
    [closeTaskEntryDialog, navigate]
  )

  // 表示形式のボタングループ
  const taskDisplayModeButtonGroup: ButtonGroupInfo[] = useMemo(() => {
    const items = Object.keys(MyTaskDisplayModeButtonGroup).map((key) => {
      const item =
        MyTaskDisplayModeButtonGroup[
          key as keyof typeof MyTaskDisplayModeButtonGroup
        ]

      const SvgIcon = item.icon

      return {
        id: item.id,
        text: undefined,
        icon: (
          <div id={`${item.id}`}>
            <SvgIcon width={24} height={24} />
            {/* ツールチップ */}
            <CrewGuideToolTip
              target={`#${item.id}`}
              position="bottom"
              type={item.tooltipType}
            />
          </div>
        ),
      }
    })

    return items
  }, [])

  // タスクリスト表示モードボタングループのアイテムクリック時
  const handleTaskDisplayModeItemClick = useCallback(
    (itemData: { id: string }) => {
      dispatch(setSelectedMyTaskListDisplayMode(itemData.id))
    },
    [dispatch]
  )

  // ツールバーアイテムの描画
  const renderToolbarItems = useCallback(() => {
    // 新規タスクボタン
    const renderNewTaskButton = () => (
      <CrewButton
        id="my-task-list-toolbar-new-task-button"
        type="primary"
        className="ml-auto"
        text={t('action.newTask')}
        onClick={openTaskEntryDialog}
      />
    )

    // タスクリスト表示モードによってツールバーアイテムを変更
    if (
      selectedTaskListDisplayMode === MyTaskDisplayModeButtonGroup.List.id ||
      selectedTaskListDisplayMode === MyTaskDisplayModeButtonGroup.Calendar.id
    ) {
      // リスト表示 または カレンダー表示 の場合は新規タスクボタンを表示
      return (
        <div className="flex flex-row items-center gap-x-1">
          {renderNewTaskButton()}
        </div>
      )
    } else {
      // それ以外の表示モードではツールバーアイテムを表示しない
      return null
    }
  }, [openTaskEntryDialog, selectedTaskListDisplayMode, t])

  return (
    <>
      <div className="flex flex-col gap-y-2">
        <div className="flex justify-between">
          {/* 表示切替ボタングループ */}
          <CrewButtonGroup
            items={taskDisplayModeButtonGroup}
            keyExpr="id"
            textExpr="text"
            iconExpr="icon"
            stylingMode="text"
            selectedItemKey={selectedTaskListDisplayMode}
            onItemClick={handleTaskDisplayModeItemClick}
          />

          {/* ツールバーアイテム */}
          {renderToolbarItems()}
        </div>

        {/* 検索条件 */}
        {/* ガントチャート表示の場合は検索ボックスを表示しない */}
        {selectedTaskListDisplayMode !==
          MyTaskDisplayModeButtonGroup.Gantt.id && <MyTaskListSearchPanel />}
      </div>

      <TaskEntryDialog
        entryFormMode={TaskEntryFormMode.New}
        title={t('label.addTaskTitle')}
        onSubmit={handleTaskRegistered}
        isOpen={isTaskEntryDialogOpen}
        onClose={closeTaskEntryDialog}
      />
    </>
  )
})
